import { useCallback, useRef } from 'react';
import { getRealtorInfo } from '../../actions/realtorInfo';
import { useDispatch, useSelector } from 'react-redux';
import { selectRealtorInfo } from '../../selectors/selectRealtorInfo';
import { CancellationToken } from '@cian/http-api';
import { ISpecialistCatalogListingSchema } from '../../repositories/demand/entities/specialist_catalog/SpecialistCatalogListingSchema';
import { setRealtorInfoSuccess } from '../../actions/realtorInfo/actions';
import { TThunkDispatch } from '../../types/redux';
import { resetCurrentRealtor, setCurrentRealtor } from '../../actions/currentRealtor';
import { selectCurrentRealtor } from '../../selectors/selectCurrentRealtor';

export const useRealtorCardData = () => {
  const dispatch = useDispatch<TThunkDispatch>();
  const realtor = useSelector(selectCurrentRealtor);

  /** для кеширования успешных результатов запроса getRealtorInfo */
  const realtorInfoMap = useRef(new Map());
  /** для сброса запроса при закрытии модального окна */
  const cancellationTokenRef = useRef<CancellationToken | undefined>(undefined);
  const {
    data: realtorCardData,
    isFetching: isRealtorCardDataFetching,
    isError: isRealtorCardDataError,
  } = useSelector(selectRealtorInfo);

  const resetRealtorCardData = useCallback(() => {
    cancellationTokenRef?.current?.cancel();
    dispatch(setRealtorInfoSuccess(null));
    dispatch(resetCurrentRealtor());
  }, [dispatch]);

  const getRealtorInfoData = useCallback(
    async (cianUserId: number) => {
      const result = await dispatch(
        getRealtorInfo({ realtorId: cianUserId, cancellationToken: cancellationTokenRef.current }),
      );
      if (result) {
        realtorInfoMap.current.set(cianUserId, result);
      }
    },
    [dispatch],
  );

  const setRealtorCardData = useCallback(
    (realtor?: ISpecialistCatalogListingSchema) => {
      if (realtor) {
        dispatch(setCurrentRealtor(realtor));
        const realtorInfoData = realtorInfoMap.current.get(realtor.cianUserId);

        if (!realtorInfoData) {
          cancellationTokenRef.current = new CancellationToken();
          getRealtorInfoData(realtor.cianUserId);
        } else {
          dispatch(setRealtorInfoSuccess(realtorInfoData));
        }
      }

      return null;
    },
    [dispatch, getRealtorInfoData],
  );

  return {
    realtor,
    resetRealtorCardData,
    setRealtorCardData,
    realtorCardData,
    isRealtorCardDataFetching,
    isRealtorCardDataError,
  };
};
